import axios from "axios";
import {
  BadgeIndianRupee,
  ChevronRight,
  Mail,
  MessageSquare,
  Settings,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Lono from "../../assets/navbar_icon.png";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import "./sidebar.css";

import {
  Affiliate,
  Bank,
  Crm,
  Dashboard,
  Finance,
  Logout as LogoutIcon,
  Report,
  Staff,
} from "../../assets/Icons";

const Sidebar = () => {
  const { sidebarExpanded, setSidebarExpanded, isMobile, setMobileMenuOpen } =
    useSidebar();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const role = user?.role || "customer";
  const currentPath = location.pathname;

  const isAdmin = role === "admin";
  const isSubAdmin = role === "subAdmin";

  const basePath = `/${role}`;

  const routeMap = {
    dashboard: `${basePath}/dashboard`,
    "loan-applications": `${basePath}/customers`,
    "banking-partner": `${basePath}/banking-partner`,
    "affiliate-partners": `${basePath}/affiliates`,
    leads: `${basePath}/leads`,
    communication: `${basePath}/communication`,
    settings:
      role === "affiliate-partner"
        ? `${basePath}/profile`
        : `${basePath}/settings`,
    staff: `${basePath}/staff`,
    reports: `${basePath}/reports`,
    financials: `${basePath}/financials`,
    attendance: `${basePath}/attendance`,
    messages: `${basePath}/messages`,
  };

  const handleNavigation = (path) => {
    const targetPath = routeMap[path] || path;
    navigate(targetPath);
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.clear();
      sessionStorage.clear();
      delete axios.defaults.headers.common["Authorization"];
      toast.success("Logged out successfully.");
     navigate("/login");
    } catch (error) {
      toast.error("Logout failed. Please try again.");
    }
  };

  if (isMobile) return null;

  const navItems = {
    admin: [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "staff", label: "Staff Management", icon: <Staff /> },
      { to: "banking-partner", label: "Banking Partners", icon: <Bank /> },
      { to: "affiliate-partners", label: "Affiliate Partners", icon: <Affiliate /> },
      { to: "financials", label: "Finance Dashboard", icon: <Finance /> },
      { to: "reports", label: "Sales Dashboard", icon: <Report /> },
      { to: "communication", label: "Communication Center", icon: <MessageSquare /> },
      { to: "messages", label: "Bulking Messages", icon: <Mail /> },
      { to: "settings", label: "Settings", icon: <Settings /> },
    ],
    subAdmin: [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "staff", label: "Staff Management", icon: <Staff /> },
      { to: "banking-partner", label: "Banking Partners", icon: <Bank /> },
      { to: "affiliate-partners", label: "Affiliate Partners", icon: <Affiliate /> },
      { to: "financials", label: "Finance Dashboard", icon: <Finance /> },
      { to: "reports", label: "Sales Dashboard", icon: <Report /> },
      { to: "communication", label: "Communication Center", icon: <MessageSquare /> },
      { to: "messages", label: "Bulking Messages", icon: <Mail /> },
    ],
    staff: [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "attendance", label: "Attendance", icon: <Affiliate /> },
    ],
    "banking-partner": [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "communication", label: "Communication Center", icon: <MessageSquare /> },
      { to: "settings", label: "Settings", icon: <Settings /> },
    ],
    "affiliate-partner": [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "leads", label: "Leads", icon: <BadgeIndianRupee /> },
      { to: "settings", label: "Settings", icon: <Settings /> },
    ],
    "credit-manager": [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "banking-partner", label: "Banking Partners", icon: <Bank /> },
      { to: "communication", label: "Communication Center", icon: <MessageSquare /> },
      { to: "attendance", label: "Attendance", icon: <Affiliate /> },
    ],
  };

  const roleNavItems = navItems[role] || [];

  return (
    <aside
      className={`fixed top-0 left-0 h-full bg-[#131313] z-20 flex flex-col items-center py-4 overflow-hidden ${
        !sidebarExpanded ? "sidebar-collapsed" : ""
      }`}
      style={{ width: sidebarExpanded ? "220px" : "80px" }}
    >
      {/* Logo */}
      <div className="mb-6">
        <img src={Lono} alt="Logo" className="w-16 h-12 rounded" />
      </div>

      {/* Expand Button */}
      <button
        onClick={() => setSidebarExpanded(!sidebarExpanded)}
        className="flex items-center justify-center w-full py-2 text-[#FFD700] font-medium mb-4 hover:bg-gray-800 rounded"
      >
        {sidebarExpanded ? (
          <div className="flex items-center text-sm">
            <span className="mr-2">Collapse</span>
            <ChevronRight className="rotate-180" size={16} />
          </div>
        ) : (
          <ChevronRight size={16} />
        )}
      </button>

      {/* Menu Items */}
      <div
        className="flex flex-col gap-2 w-full items-center overflow-hidden sidebar-menu-container"
        style={{ maxHeight: "calc(100vh - 200px)" }}
      >
        {roleNavItems.map(({ to, label, icon }) => {
          const targetPath = routeMap[to] || to;
          const isActive = currentPath.startsWith(targetPath);

          return (
            <NavItem
              key={to}
              to={to}
              icon={icon}
              label={label}
              expanded={sidebarExpanded}
              active={isActive}
              onClick={handleNavigation}
            />
          );
        })}
      </div>

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="mt-auto mb-4 text-red-500 px-4 py-2 rounded-lg hover:text-black hover:bg-red-500 transition-colors sticky bottom-0"
      >
        <div className="flex items-center gap-2">
          <LogoutIcon size={sidebarExpanded ? 20 : 18} />
          {sidebarExpanded && <span>Log Out</span>}
        </div>
      </button>
    </aside>
  );
};

const NavItem = ({ to, icon, label, expanded, active = false, onClick }) => (
  <button
    onClick={() => onClick(to)}
    className={`flex items-center justify-${
      expanded ? "start" : "center"
    } w-full px-3 py-2
      ${
        active
          ? "text-black bg-[#FFD700]"
          : "text-[#FFD700] hover:bg-[#FFD700] hover:text-black"
      }
      transition-colors relative flex-shrink-0`}
    style={{
      width: expanded ? "90%" : "70%",
      borderRadius: "4px",
      minHeight: expanded ? "36px" : "40px",
    }}
  >
    <div
      className={`flex items-center ${
        expanded ? "justify-start w-full" : "justify-center"
      }`}
    >
      {icon}
      {expanded && (
        <span className="ml-3 text-sm font-medium truncate whitespace-nowrap overflow-hidden max-w-[130px]">
          {label}
        </span>
      )}
    </div>
  </button>
);

export default Sidebar;
